import React, { useMemo, useState } from "react";
import {
  Typography,
  Box,
  Button,
} from "@mui/material";
import Grid2 from "@mui/material/Grid2";
import AddressCard from "./address/AddressCard";
import AddressDialog from "./address/AddressDialog";
import AddressEmptyState from "./address/AddressEmptyState";
import { cryptoRandomId, emptyAddress, validateAddress } from "./address/addressUtils";

/**
 * Props:
 *  - addresses: Array<Address>
 *  - onAddressChange: (updated: Address[]) => void
 *
 * Address:
 *  { id, label, name, street, zip, city, country, forOutgoingInvoice }
 */
export default function AddressModule({
  onAddressChange,
  addresses,
  showOutgoingCheckbox = true,
  validationErrors = {},
}) {
  const [openDialog, setOpenDialog] = useState(false);
  const [editingIndex, setEditingIndex] = useState(null);
  const [currentAddress, setCurrentAddress] = useState(emptyAddress());
  const [fieldErrors, setFieldErrors] = useState({});

  const selectedIndex = useMemo(
    () => (addresses ? addresses.findIndex((a) => a.forOutgoingInvoice) : -1),
    [addresses]
  );

  /* ===== Dialog open/close ===== */
  const handleDialogOpen = () => {
    setCurrentAddress(emptyAddress());
    setEditingIndex(null);
    setFieldErrors({});
    setOpenDialog(true);
  };
  const handleDialogClose = () => {
    setOpenDialog(false);
    setCurrentAddress(emptyAddress());
    setEditingIndex(null);
    setFieldErrors({});
  };

  /* ===== CRUD ===== */
  const handleSave = () => {
    const nextErrors = validateAddress(currentAddress);
    if (Object.keys(nextErrors).length > 0) {
      setFieldErrors(nextErrors);
      return;
    }

    const updated = [...addresses];
    const withId = {
      ...currentAddress,
      id: currentAddress.id || cryptoRandomId(),
    };

    if (withId.forOutgoingInvoice) {
      for (let i = 0; i < updated.length; i++) {
        updated[i] = { ...updated[i], forOutgoingInvoice: false };
      }
    }

    if (editingIndex != null) {
      updated[editingIndex] = withId;
    } else {
      updated.push(withId);
    }

    onAddressChange(updated);
    handleDialogClose();
  };

  const handleEdit = (index) => {
    setCurrentAddress(addresses[index] || emptyAddress());
    setEditingIndex(index);
    setFieldErrors(validationErrors?.[index] || {});
    setOpenDialog(true);
  };

  const handleDelete = (index) => {
    const updated = addresses.filter((_, i) => i !== index);
    onAddressChange(updated);
  };

  const handleCheckboxChange = (index) => {
    const isSelected = addresses[index]?.forOutgoingInvoice;
    const updated = addresses.map((addr, i) => ({
      ...addr,
      forOutgoingInvoice: i === index ? !isSelected : false,
    }));
    onAddressChange(updated);
  };

  const handleFieldChange = (field, value) => {
    setCurrentAddress((prev) => ({ ...prev, [field]: value }));
    if (fieldErrors[field]) {
      setFieldErrors((prev) => {
        const next = { ...prev };
        delete next[field];
        return next;
      });
    }
  };

  return (
    <Box sx={{ mt: 1 }}>
      <Box
        sx={{
          mb: 1.5,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2,
          flexWrap: "wrap",
        }}
      >
        <Typography variant="h6" fontWeight={700}>
          Adressen
        </Typography>
        <Button
          variant="contained"
          onClick={handleDialogOpen}
          sx={{
            bgcolor: "orange",
            "&:hover": { bgcolor: "#cc7a00" },
            borderRadius: 2,
          }}
        >
          Adresse hinzufügen
        </Button>
      </Box>

      <Grid2 container spacing={2}>
        {addresses?.map((address, index) => (
          <AddressCard
            key={address.id || index}
            address={address}
            index={index}
            validationErrors={validationErrors?.[index]}
            selectedIndex={selectedIndex}
            showOutgoingCheckbox={showOutgoingCheckbox}
            onEdit={handleEdit}
            onDelete={handleDelete}
            onToggleOutgoing={handleCheckboxChange}
          />
        ))}

        {(!addresses || addresses.length === 0) && <AddressEmptyState />}
      </Grid2>

      <AddressDialog
        open={openDialog}
        isEditing={editingIndex != null}
        currentAddress={currentAddress}
        fieldErrors={fieldErrors}
        onClose={handleDialogClose}
        onSave={handleSave}
        onFieldChange={handleFieldChange}
        onToggleOutgoing={(checked) =>
          setCurrentAddress({ ...currentAddress, forOutgoingInvoice: checked })
        }
      />
    </Box>
  );
}
